import React, { useMemo, useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

// Simple INR currency formatter (Indian locale grouping)
const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 });

// Map label -> compounding periods per year
const FREQ = {
  "Yearly (1x)": 1,
  "Half-yearly (2x)": 2,
  "Quarterly (4x)": 4,
  "Monthly (12x)": 12,
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function maturityAmount({ principal, ratePct, years, n }) {
  const r = ratePct / 100; // decimal
  if (years <= 0) return principal;
  if (n <= 0) return principal * (1 + r * years); // fallback
  return principal * Math.pow(1 + r / n, n * years);
}

function simpleInterestAmount({ principal, ratePct, years }) {
  const r = ratePct / 100;
  return principal * (1 + r * years);
}

function effectiveAnnualRate(ratePct, n) {
  const r = ratePct / 100;
  if (n <= 0) return r;
  return Math.pow(1 + r / n, n) - 1;
}

function buildSeries({ principal, ratePct, years, n }) {
  const r = ratePct / 100;
  const steps = Math.max(1, Math.floor(n * years));
  const data = [];

  // Start point
  data.push({ idx: 0, label: "Start", value: principal });

  for (let k = 1; k <= steps; k++) {
    const tYears = k / n;
    const val = principal * Math.pow(1 + r / n, k);
    const label = n === 12 ? `M${k}` : n === 4 ? `Q${k}` : n === 2 ? `H${k}` : `Y${k}`;
    if (tYears <= years + 1e-9) {
      data.push({ idx: k, label, value: val });
    }
  }

  // If tenure not an integer number of periods, add final point precisely at maturity
  const exact = principal * Math.pow(1 + r / n, n * years);
  const last = data[data.length - 1];
  if (!last || Math.abs(last.value - exact) > 0.01) {
    data.push({ idx: data.length, label: "Maturity", value: exact });
  }
  return data.map(d => ({ ...d, value: round2(d.value) }));
}

function Stat({ label, value, sub }) {
  return (
    <div className="p-4 rounded-2xl border bg-white shadow-sm">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
      {sub ? <div className="text-xs text-gray-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
        {label}
        {hint && <span className="text-xs font-normal text-gray-500">{hint}</span>}
      </div>
      <div className="mt-2">{children}</div>
    </label>
  );
}

export default function FDCalculator() {
  const [principal, setPrincipal] = useState(100000);
  const [ratePct, setRatePct] = useState(7.5);
  const [years, setYears] = useState(1);
  const [months, setMonths] = useState(6);
  const [freqKey, setFreqKey] = useState("Quarterly (4x)");
  const [showSimple, setShowSimple] = useState(false);

  // normalize
  const n = FREQ[freqKey];
  const tenureYears = clamp(years, 0, 100) + clamp(months, 0, 11) / 12;

  const results = useMemo(() => {
    const maturity = maturityAmount({ principal, ratePct, years: tenureYears, n });
    const interest = maturity - principal;
    const ear = effectiveAnnualRate(ratePct, n);
    const simple = simpleInterestAmount({ principal, ratePct, years: tenureYears });
    const series = buildSeries({ principal, ratePct, years: tenureYears, n });
    return { maturity, interest, ear, simple, series };
  }, [principal, ratePct, tenureYears, n]);

  // Support query params (?p=100000&r=7.5&y=1&m=6&f=quarterly)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = parseFloat(params.get("p"));
    const r = parseFloat(params.get("r"));
    const y = parseFloat(params.get("y"));
    const mo = parseFloat(params.get("m"));
    const f = (params.get("f") || "").toLowerCase();

    if (!Number.isNaN(p)) setPrincipal(clamp(p, 0, 1e9));
    if (!Number.isNaN(r)) setRatePct(clamp(r, 0, 100));
    if (!Number.isNaN(y)) setYears(clamp(y, 0, 100));
    if (!Number.isNaN(mo)) setMonths(clamp(mo, 0, 11));

    const map = {
      yearly: "Yearly (1x)",
      halfyearly: "Half-yearly (2x)",
      half: "Half-yearly (2x)",
      quarterly: "Quarterly (4x)",
      monthly: "Monthly (12x)",
    };
    if (map[f]) setFreqKey(map[f]);
  }, []);

  const copyShare = () => {
    const map = {
      1: "yearly",
      2: "halfyearly",
      4: "quarterly",
      12: "monthly",
    };
    const url = new URL(window.location.href);
    url.searchParams.set("p", String(principal));
    url.searchParams.set("r", String(ratePct));
    url.searchParams.set("y", String(years));
    url.searchParams.set("m", String(months));
    url.searchParams.set("f", map[n] || "yearly");
    navigator.clipboard.writeText(url.toString());
    alert("Sharable link copied to clipboard!");
  };

  const reset = () => {
    setPrincipal(100000);
    setRatePct(7.5);
    setYears(1);
    setMonths(6);
    setFreqKey("Quarterly (4x)");
    setShowSimple(false);
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 text-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <header className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Fixed Deposit (FD) Calculator</h1>
            <p className="text-gray-600 mt-1">Fast and accurate maturity estimates with configurable compounding.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={copyShare} className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-100 text-sm">Share</button>
            <button onClick={reset} className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-100 text-sm">Reset</button>
          </div>
        </header>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-1 p-5 rounded-2xl border bg-white shadow-sm">
            <div className="space-y-5">
              <Field label="Principal (Deposit)">
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={100}
                  value={principal}
                  onChange={(e) => setPrincipal(clamp(parseFloat(e.target.value || "0"), 0, 1e12))}
                  className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </Field>

              <Field label="Annual Interest Rate" hint="in % p.a.">
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    max={100}
                    step={0.05}
                    value={ratePct}
                    onChange={(e) => setRatePct(clamp(parseFloat(e.target.value || "0"), 0, 100))}
                    className="w-40 rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="range"
                    min={0}
                    max={15}
                    step={0.05}
                    value={clamp(ratePct, 0, 15)}
                    onChange={(e) => setRatePct(clamp(parseFloat(e.target.value || "0"), 0, 100))}
                    className="flex-1"
                  />
                </div>
              </Field>

              <Field label="Tenure">
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={1}
                      value={years}
                      onChange={(e) => setYears(clamp(parseInt(e.target.value || "0"), 0, 100))}
                      className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">Years</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={11}
                      step={1}
                      value={months}
                      onChange={(e) => setMonths(clamp(parseInt(e.target.value || "0"), 0, 11))}
                      className="w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-600">Months</span>
                  </div>
                </div>
              </Field>

              <Field label="Compounding Frequency">
                <select
                  value={freqKey}
                  onChange={(e) => setFreqKey(e.target.value)}
                  className="w-full rounded-xl border px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.keys(FREQ).map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
              </Field>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={showSimple} onChange={(e) => setShowSimple(e.target.checked)} />
                Compare with Simple Interest (no compounding)
              </label>

              <div className="text-xs text-gray-500 leading-relaxed">
                <p>Results are estimates. Actual bank payouts may differ based on day counts and policies.</p>
              </div>
            </div>
          </div>

          {/* Right: Summary + Chart */}
          <div className="lg:col-span-2 space-y-6">
            <div className="grid md:grid-cols-3 gap-4">
              <Stat label="Maturity Amount" value={inr.format(results.maturity)} sub={`at ${freqKey.toLowerCase()}`} />
              <Stat label="Total Interest Earned" value={inr.format(results.interest)} sub={`${(results.interest / principal * 100).toFixed(2)}% of principal`} />
              <Stat label="Effective Annual Yield (EAR)" value={(results.ear * 100).toFixed(2) + "% p.a."} sub={`from ${ratePct}% with ${freqKey.toLowerCase()}`} />
            </div>

            {showSimple && (
              <div className="p-4 rounded-2xl border bg-amber-50">
                <div className="text-sm text-gray-800">
                  <strong>Simple Interest Comparison:</strong> {inr.format(results.simple)} maturity
                  <span className="ml-2 text-gray-600">(no compounding).</span>
                </div>
              </div>
            )}

            <div className="p-5 rounded-2xl border bg-white shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Growth Over Time</h2>
                <div className="text-sm text-gray-600">Values at each compounding step</div>
              </div>

              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={results.series} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => inr.format(v).replace("₹", "₹ ")} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => inr.format(v)} labelFormatter={(l) => `Step: ${l}`} />
                    <Line type="monotone" dataKey="value" strokeWidth={3} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="p-5 rounded-2xl border bg-white shadow-sm">
              <h3 className="text-base font-semibold mb-2">Assumptions</h3>
              <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                <li>Compounding uses the standard formula: <code>A = P × (1 + r/n)<sup>n·t</sup></code>.</li>
                <li>Tenure can include months; e.g., 1 year 6 months = 1.5 years.</li>
                <li>EAR (Effective Annual Rate) shown for your chosen compounding frequency.</li>
                <li>Simple-interest comparison assumes no compounding over the tenure.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
